import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';

interface User {
  userId: string;
  email: string;
  status: string;
  plan: string;
  quota_gb: number;
  expire_time: string;
  download_bytes: number;
  upload_bytes: number;
  created_at: string;
}

export default function Admin() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'expired'>('active');
  const [sortField, setSortField] = useState<keyof User | 'usage'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const navigate = useNavigate();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await client.get('/admin/users');
      setUsers(res.data.users || []);
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('无权限访问管理页面');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError('加载用户列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const isExpired = (expireTime: string) => {
    return new Date(expireTime) < new Date();
  };

  const handleSort = (field: keyof User | 'usage') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedUsers = (usersToSort: User[]) => {
    return [...usersToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortField === 'usage') {
        aValue = (a.download_bytes || 0) + (a.upload_bytes || 0);
        bValue = (b.download_bytes || 0) + (b.upload_bytes || 0);
      } else if (sortField === 'expire_time' || sortField === 'created_at') {
        aValue = new Date(a[sortField]).getTime();
        bValue = new Date(b[sortField]).getTime();
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredUsers = users.filter(user => {
    if (filter === 'active') return user.status === 'active' && !isExpired(user.expire_time);
    if (filter === 'expired') return user.status === 'active' && isExpired(user.expire_time);
    return true;
  });

  const sortedUsers = getSortedUsers(filteredUsers);

  const activeUsers = users.filter(u => u.status === 'active' && !isExpired(u.expire_time));
  const totalTraffic = users.reduce((sum, u) => sum + (u.download_bytes || 0) + (u.upload_bytes || 0), 0);

  if (loading) return <div style={styles.center}>加载中...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>管理后台</h1>
        <button style={styles.logoutBtn} onClick={() => navigate('/dashboard')}>
          返回用户页面
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* 统计卡片 */}
      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>总用户数</div>
          <div style={styles.statValue}>{users.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>活跃用户</div>
          <div style={styles.statValue}>{activeUsers.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>总流量使用</div>
          <div style={styles.statValue}>{formatBytes(totalTraffic)}</div>
        </div>
      </div>

      {/* 过滤器 */}
      <div style={styles.filterContainer}>
        <button
          style={{ ...styles.filterBtn, ...(filter === 'all' ? styles.filterBtnActive : {}) }}
          onClick={() => setFilter('all')}
        >
          全部 ({users.length})
        </button>
        <button
          style={{ ...styles.filterBtn, ...(filter === 'active' ? styles.filterBtnActive : {}) }}
          onClick={() => setFilter('active')}
        >
          活跃 ({activeUsers.length})
        </button>
        <button
          style={{ ...styles.filterBtn, ...(filter === 'expired' ? styles.filterBtnActive : {}) }}
          onClick={() => setFilter('expired')}
        >
          已过期 ({users.filter(u => u.status === 'active' && isExpired(u.expire_time)).length})
        </button>
      </div>

      {/* 用户表格 */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th} onClick={() => handleSort('email')}>
                邮箱 {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th style={styles.th}>状态</th>
              <th style={styles.th} onClick={() => handleSort('plan')}>
                套餐 {sortField === 'plan' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th style={styles.th} onClick={() => handleSort('expire_time')}>
                到期时间 {sortField === 'expire_time' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th style={styles.th} onClick={() => handleSort('quota_gb')}>
                配额 {sortField === 'quota_gb' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th style={styles.th} onClick={() => handleSort('usage')}>
                已用流量 {sortField === 'usage' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th style={styles.th}>使用率</th>
              <th style={styles.th} onClick={() => handleSort('created_at')}>
                注册时间 {sortField === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.length === 0 ? (
              <tr>
                <td colSpan={8} style={styles.noData}>暂无数据</td>
              </tr>
            ) : (
              sortedUsers.map((user) => {
                const usedBytes = (user.download_bytes || 0) + (user.upload_bytes || 0);
                const totalBytes = (user.quota_gb || 0) * 1024 * 1024 * 1024;
                const usagePercent = totalBytes > 0 ? (usedBytes / totalBytes * 100).toFixed(1) : '0';
                const expired = isExpired(user.expire_time);

                return (
                  <tr key={user.userId} style={styles.tableRow}>
                    <td style={styles.td}>{user.email}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        ...(user.status === 'inactive' ? styles.badgeInactive : 
                            expired ? styles.badgeExpired : styles.badgeActive)
                      }}>
                        {user.status === 'inactive' ? '异常' : 
                         expired ? '过期' : '活跃'}
                      </span>
                    </td>
                    <td style={styles.td}>{user.plan}</td>
                    <td style={styles.td}>
                      <span style={expired ? styles.expiredText : {}}>
                        {user.expire_time?.split('T')[0]}
                      </span>
                    </td>
                    <td style={styles.td}>{user.quota_gb} GB</td>
                    <td style={styles.td}>{formatBytes(usedBytes)}</td>
                    <td style={styles.td}>
                      <span style={parseFloat(usagePercent) > 80 ? styles.highUsage : {}}>
                        {usagePercent}%
                      </span>
                    </td>
                    <td style={styles.td}>{user.created_at?.split('T')[0]}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '20px',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontSize: '18px',
    color: '#666',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    background: '#fff',
    padding: '20px 30px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    color: '#333',
    fontWeight: '600',
  },
  logoutBtn: {
    padding: '10px 20px',
    background: '#667eea',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  error: {
    background: '#fff2f0',
    color: '#ff4d4f',
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #ffccc7',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    background: '#fff',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  statLabel: {
    fontSize: '14px',
    color: '#999',
    marginBottom: '8px',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#667eea',
  },
  filterContainer: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
  },
  filterBtn: {
    padding: '10px 20px',
    background: '#fff',
    border: '2px solid #e8e8e8',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    color: '#666',
    transition: 'all 0.2s',
  },
  filterBtnActive: {
    background: '#667eea',
    borderColor: '#667eea',
    color: '#fff',
  },
  tableContainer: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    background: '#f8f9fa',
  },
  th: {
    padding: '16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
    borderBottom: '2px solid #e8e8e8',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background 0.2s',
  },
  tableRow: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.2s',
  },
  td: {
    padding: '16px',
    fontSize: '14px',
    color: '#333',
  },
  noData: {
    padding: '40px',
    textAlign: 'center',
    color: '#999',
    fontSize: '14px',
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  badgeActive: {
    background: '#e6f7ff',
    color: '#1890ff',
  },
  badgeExpired: {
    background: '#fff7e6',
    color: '#fa8c16',
  },
  badgeInactive: {
    background: '#fff2f0',
    color: '#ff4d4f',
  },
  expiredText: {
    color: '#ff4d4f',
    fontWeight: '600',
  },
  highUsage: {
    color: '#ff4d4f',
    fontWeight: '600',
  },
};
